/**
 * Legacy "Change History" list of {@link ChangeRequestsTab} — the rows that
 * carry no `change_request_ref` (pre-CR-4865 data). Once every historical row
 * has been resolved this section stays empty forever.
 *
 * Moved out of ChangeRequestsTab unchanged for Customer review 11 Sep (review
 * msg 28), which split that file under the 500-line limit.
 */
import { Edit, User } from 'lucide-react';
import { ChangelogEntry } from '../common/Changelog';
import { getOtifDateVariance } from '../../utils/otifDateVariance';
import { FIELD_ICONS, STATUS_DISPLAY, getHistoryStatusBadge } from './changeRequestsTabHelpers';

interface LegacyChangeHistoryProps {
  entries: ChangelogEntry[];
  /** The line's ODD, for colouring promise-date rows (CR-5479). */
  odd?: string;
  otifWindow?: { days_early: number; days_late: number } | null;
  getFieldLabel: (field: string) => string;
  formatDateTime: (value: string) => string;
}

export default function LegacyChangeHistory({ entries, odd, otifWindow, getFieldLabel, formatDateTime }: LegacyChangeHistoryProps) {
  if (entries.length === 0) return null;
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Change History</h4>
      {entries.map((entry, index) => {
        const FieldIcon = FIELD_ICONS[entry.field_changed] || Edit;
        const statusBadge = getHistoryStatusBadge(entry);
        return (
          <div key={entry.id || index} className="border rounded-lg p-3 bg-white shadow-sm">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <FieldIcon className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="font-medium text-sm">{getFieldLabel(entry.field_changed)}</span>
                {statusBadge}
              </div>
              <div className="text-xs text-muted-foreground text-right">
                {formatDateTime(entry.change_date)}
              </div>
            </div>
            {entry.field_changed === 'acknowledge_status' ? (
              <div className="text-sm">
                <span className="text-muted-foreground">{STATUS_DISPLAY[entry.old_value || ''] || entry.old_value}</span>
                <span className="mx-2 text-muted-foreground">&rarr;</span>
                <span className="font-medium">{STATUS_DISPLAY[entry.new_value] || entry.new_value}</span>
              </div>
            ) : (
              <div className="text-sm">
                <span className="text-muted-foreground">{entry.old_value || 'N/A'}</span>
                <span className="mx-2 text-muted-foreground">&rarr;</span>
                {(() => {
                  // CR-5479: colour-code a promise-date history row's new
                  // value against the ODD + OTIF window, same as the table.
                  const v = entry.field_changed === 'supplier_promise_date'
                    ? getOtifDateVariance(entry.new_value, odd, otifWindow)
                    : null;
                  return (
                    <span className={v?.className ? `inline-block px-1.5 py-0.5 rounded font-medium ${v.className}` : 'font-medium'}>
                      {entry.new_value}{v?.suffix || ''}
                    </span>
                  );
                })()}
              </div>
            )}
            {entry.reason && (
              <div className="text-xs text-muted-foreground mt-1">Reason: {entry.reason}</div>
            )}
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <User className="h-3 w-3" />
              {entry.changed_by}
            </div>
          </div>
        );
      })}
    </div>
  );
}
